import React, {useEffect, useRef, useState} from "react";
import {useIntl} from "react-intl";
import {useDispatch, useSelector} from "react-redux";

//Components
import {Button, Form, Icons, Select, SidePanel, SidePanelBody, SidePanelFooter, SidePanelHeader, Switch, Tooltip} from '@gpstrackit/gtc-react-controls';
import {NotificationManager} from "Components/Notifications";
import {bulkCreateDriverPerformanceCasesFull, openModalCreateDriverCoaching, reset} from "./redux/slice";
import './style.scss';


const CreateDriverCoaching = () => {
    const dispatch = useDispatch();
    const { messages } = useIntl();

    // Redux State
    const { loading, open, loadingCreate, error, drivers } = useSelector(state => state.driverPerformanceDriverTabCreateDriverCoachingRedux || {});
    const eventIds = useSelector(state => state.driverPerformanceDriverTabCreateDriverCoachingRedux?.eventIds);
    const isEvent = useSelector(state => state.driverPerformanceDriverTabCreateDriverCoachingRedux?.isEvent);

    // Local State
    const [data, setData] = useState({includeQueueForCoaching: false});
    const [enableSend, setEnableSend] = useState({});


    // Refs
    const prevLoadingCreateRef = useRef(false);

    const formRef = Form.useForm();
    const SEVERITY_SCORE_OPTIONS = [
        {id: 10}, {id: 20}, {id: 30}, {id: 40}, {id: 50},
        {id: 60}, {id: 70}, {id: 80}, {id: 90}
    ];
    const FREQUENCY_OPTIONS = [
        {id: 1, label: '1'}, {id: 2, label: '2'}, {id: 3, label: '3'},
        {id: 4, label: '4'}, {id: 5, label: '5'},
        {id: -99, label: messages['driver_performance_cases_create_all']}
    ];
    const MAX_EVENTS_OPTIONS = [
        {id: 1, label: '1'}, {id: 2, label: '2'}, {id: 3, label: '3'},
        {id: 4, label: '4'}, {id: 5, label: '5'},
        { id: -99, label: messages['driver_performance_cases_create_all'] }
    ];

    useEffect(() => {
        if (open) {
            setData({includeQueueForCoaching: false})
        }
    }, [open])

    useEffect(() => {
        if (data) {
            setEnableSend(validateFields());
        }
    }, [data])


    useEffect(() => {
        const prevLoadingCreate = prevLoadingCreateRef.current;
        // Only notify when an update cycle finished: loadingCreate true -> false
        if (prevLoadingCreate && !loadingCreate) {
            if (error) {
                NotificationManager.error(
                    error?.error || messages['errorMessage'],
                    messages['driver_performance_cases_create_saveError'],
                    3000,
                    null,
                    null,
                    'error',
                );
            } else {
                NotificationManager.success(
                    messages['success'],
                    messages['driver_performance_cases_create_saveSuccessfully'],
                    3000,
                    null,
                    null,
                    'success',
                );
            }
        }
        prevLoadingCreateRef.current = loadingCreate;
    }, [loadingCreate, error])

    const validateFields = () => {
        const validateFields = ['byFrequency', 'maxEventsPerType'];
        return validateFields.every(field => data[field]);
    }

    const handleSave = () => {
        if (data) {
            let payload = {
                driverIds: drivers,
                eventCategories: data.byFrequency?.id,
                eventsLimit: data.maxEventsPerType?.id,
                includeHighlight: data.includeQueueForCoaching,

                // TODO: We will leave these 2 properties disabled for the next scope
                // severityScore: data.severityScore?.id,

                driverScope: 0,
                limitedBy: 0
            }
            if (isEvent) {
                payload = {
                    ...payload,
                    eventIds: eventIds
                }
            }

            dispatch(bulkCreateDriverPerformanceCasesFull(payload));
        }
        dispatch(openModalCreateDriverCoaching({ open: false }));
    }

    const handleCancel = () => {
        dispatch(openModalCreateDriverCoaching({ open: false }));
    }

    return (
        <SidePanel isOpen={open} onToggle={handleCancel} hiddenButton={true} style={{ width: '350px', transform: 'translateX(350px)' }}>
            <SidePanelHeader>
                <label className="create-casesTap-panel__title">{isEvent ? messages['driver_performance_driverCoaching_title_events'] : messages['driver_performance_driverCoaching_title']}</label>
            </SidePanelHeader>
            <SidePanelBody>
                <Form
                    ref={formRef.ref}
                    loading={loading}
                    onValidSubmit={() => {
                        if (validateFields()) {
                            handleSave();
                        }
                    }}
                    onChangeValues={() => { }}
                    className="cases"
                >
                    <div style={{display: 'flex', alignItems: 'baseline', marginBottom: 20}}>
                        <strong style={{ marginRight: 5 }}>{drivers.length ? `${drivers.length} ` : ""}</strong>
                        <span style={{ fontSize: 14, fontWeight: 400 }}>
                            {
                                // Determine the message based on isEvent flag and driver count
                                isEvent
                                    ? (drivers?.length > 1
                                        ? `${messages['driver_performance_driverCoaching_events_count']}`
                                        : `${messages['driver_performance_driverCoaching_events_single_count']}`)
                                    : (drivers?.length > 1
                                        ? `${messages['driver_performance_driverCoaching_count']}`
                                        : `${messages['driver_performance_driverCoaching_single_count']}`)

                        }
                        </span>
                    </div>

                    {/* Event Types to Include Section */}
                    <div className="create-coaching-section">
                        <h4 className="create-coaching-section-header">{messages['driver_performance_driverCoaching_eventTypesToInclude']}</h4>

                        <div className="create-coaching-switch-container">
                            <strong className={'create-casesTab-info-strong'}>
                                {messages['driver_performance_driverCoaching_eventStatusQueueForCoaching']}
                            </strong>

                            <Tooltip
                                className="create-coaching-tooltip-switch"
                                placement="right"
                                title={messages['driver_performance_driverCoaching_eventStatusQueueForCoachingDetail']}>
                                <Icons name="help" size="small"/>
                            </Tooltip>
                        </div>

                        <div style={{marginBottom: '10px'}}>
                            <Switch
                                checked={data?.includeQueueForCoaching || false}
                                id="includeQueueForCoaching"
                                labelPosition="top"
                                name="includeQueueForCoaching"
                                onChange={(value) => setData({...data, includeQueueForCoaching: value})}
                                size="small"
                            />
                        </div>

                        <div className="create-coaching-field vjs-hidden">
                            <strong className={'create-casesTab-info-strong'}>
                                {messages['driver_performance_driverCoaching_severityScore']}
                                <Tooltip
                                    className="create-coaching-tooltip"
                                    placement="bottom"

                                    style={{ textAlign: 'center', paddingLeft: '7px' }}
                                    title={messages['driver_performance_driverCoaching_severityScoreDetail']}>
                                    <Icons name="help" size="small" />
                                </Tooltip>
                            </strong>
                            <Select
                                id="severityScore"
                                value={data.severityScore}
                                onChange={(value) => setData({...data, severityScore: value})}
                                options={SEVERITY_SCORE_OPTIONS}
                                textField="id"
                                valueField="id"
                                placeholder={messages['driver_performance_cases_create_select']}
                                loading={loading}
                            />
                        </div>

                        <div className="create-coaching-field">
                            <strong className={'create-casesTab-info-strong'}>
                                {messages['driver_performance_driverCoaching_byFrequency']}
                                <span className="text-danger create-casesTab-info-strong-required"> *</span>
                                <Tooltip
                                    className="create-coaching-tooltip"
                                    placement="bottom"
                                    title={messages['driver_performance_driverCoaching_byFrequencyDetail']}>
                                    <Icons name="help" size="small" />
                                </Tooltip>
                            </strong>
                            <Select
                                id="byFrequency"
                                value={data.byFrequency}
                                onChange={(value) => setData({...data, byFrequency: value})}
                                options={FREQUENCY_OPTIONS}
                                textField="label"
                                valueField="id"
                                placeholder={messages['driver_performance_cases_create_select']}
                                loading={loading}
                            />
                        </div>

                        <div className="create-coaching-note">
                            {messages['driver_performance_driverCoaching_noteEventsCaped']}
                        </div>
                    </div>

                    {/* Limit Examples Section */}
                    <div className="create-coaching-section">
                        <h4 className="create-coaching-section-header">{messages['driver_performance_driverCoaching_limitExamples']}</h4>

                        <div className="create-coaching-field">
                            <strong className={'create-casesTab-info-strong'}>
                                {messages['driver_performance_driverCoaching_maximumEventsPerEventType']}
                                <span className="text-danger create-casesTab-info-strong-required"> *</span>
                                <Tooltip
                                    className="create-coaching-tooltip"
                                    placement="bottom"
                                    title={messages['driver_performance_driverCoaching_maximumEventsPerEventTypeDetail']}>
                                    <Icons name="help" size="small" />
                                </Tooltip>
                            </strong>
                            <Select
                                id="maxEventsPerType"
                                value={data.maxEventsPerType}
                                onChange={(value) => setData({...data, maxEventsPerType: value})}
                                options={MAX_EVENTS_OPTIONS}
                                textField="label"
                                valueField="id"
                                placeholder={messages['driver_performance_cases_create_select']}
                                loading={loading}
                            />
                        </div>
                    </div>

                    <div className="create-coaching-note-final">
                        <strong>{messages['driver_performance_cases_create_note']}: </strong>
                        <span>{messages['driver_performance_driverCoaching_note']}</span>
                    </div>
                </Form>
            </SidePanelBody>
            <SidePanelFooter>
                <div className="create-coaching-footer">
                    <Button
                        isDisabled={loading}
                        onClick={() => dispatch(reset({ open: false }))}
                        variant="secondary">
                        {messages["cancel"]}
                    </Button>
                    <Button
                        isDisabled={loading || !enableSend}
                        onClick={() => formRef?.submit()}
                        variant="primary">
                        {messages["driver_performance_cases_create_send"]}
                    </Button>
                </div>
            </SidePanelFooter>
        </SidePanel>
    )
}

export default CreateDriverCoaching
